// src/hooks/use-auth-api.ts
import { useState, useEffect } from 'react';

interface User {
  id: number;
  phone: string;
  name: string | null;
  email?: string | null;
  role: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useAuthApi() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // ── ابتدا از localStorage بخوان ──────────────────────
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        // ادامه برای بروزرسانی از سرور
      } catch (e) {
        // ignore
      }
    }

    const session = localStorage.getItem('auth_session');
    if (!session) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const response = await fetch('https://fellfell.ir/api/auth.php?action=me', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.data) {
        // ── ذخیره در localStorage ──────────────────────────
        localStorage.setItem('auth_user', JSON.stringify(data.data));
        
        setState({
          user: data.data,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        localStorage.removeItem('auth_session');
        localStorage.removeItem('auth_user');
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      localStorage.removeItem('auth_session');
      localStorage.removeItem('auth_user');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: (error as Error).message,
      });
    }
  };

  const login = async (phone: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch('https://fellfell.ir/api/auth.php?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone })
      });
      const data = await response.json();

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));

      return data.data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: (error as Error).message,
      }));
      throw error;
    }
  };

  const verifyOtp = async (phone: string, code: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch('https://fellfell.ir/api/auth.php?action=verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone, code })
      });
      const data = await response.json();

      if (data.success && data.data) {
        const { token, user } = data.data;
        
        const sessionData = {
          token,
          user,
          expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000)
        };
        localStorage.setItem('auth_session', JSON.stringify(sessionData));
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(user));

        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        return user;
      } else {
        throw new Error(data.message || 'خطا در تأیید کد');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: (error as Error).message,
      }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch('https://fellfell.ir/api/auth.php?action=logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_session');
      localStorage.removeItem('auth_user');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  };

  return {
    ...state,
    login,
    verifyOtp,
    logout,
    isAdmin: state.user?.role === 'admin' || state.user?.role === 'manager',
    refresh: checkAuth,
  };
}